import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  // Check if redirected due to session expiry
  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setSessionExpired(true);
      toast.warning('Your session has expired. Please login again.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      // Store token and user info
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('role', response.data.role);
      localStorage.setItem('username', response.data.username);
      
      toast.success(`Welcome ${response.data.username}!`);
      
      // Clear session expired state
      setSessionExpired(false);
      
      // Call parent onLogin function
      if (onLogin) {
        onLogin(response.data);
      }
      
      // Navigate to dashboard
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#7c3aed' }}>
            Member Management System
          </h1>
          <p className="text-gray-600">Magazine Subscription Management</p>
        </div>

        <div className="form-section">
          {sessionExpired && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center text-yellow-700">
              <AlertTriangle size={20} className="mr-2 flex-shrink-0" />
              <span className="text-sm">Your session has expired. Please login again to continue.</span>
            </div>
          )}
          
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: '#7c3aed' }}>
            Login
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Enter your username"
                  autoFocus
                  data-testid="username-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                style={{ backgroundColor: '#7c3aed' }}
                data-testid="login-button"
              >
                <LogIn className="mr-2" size={18} />
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
